import React, { useState } from "react";
import { BiFile, BiExport } from "react-icons/bi";
import { useDispatch, useSelector } from "react-redux";
import { CSVLink } from "react-csv"; // Importamos el componente de exportación
import oDayJs from "dayjs"; // Para poner la fecha dinámica al archivo descargado

import { BUTTON_CONFIG } from "config/constants/Button";
import { Button, Checkbox, ComboBox, DateInput, Table, ConfirmationModal } from "components";

import { CastDate } from "utils/SharedUtils";
import { DATES_FORMATS } from "config/constants/Dates";
import { PartnerBalanceMapper } from "../mappers/ReportMapper";
import { actionReportGetPartnersBalance, selectReportsState } from "../redux";

const PartnersBalanceReport = () => {
  const oDispatch = useDispatch();
  const { aData } = useSelector(selectReportsState);

  const onGenerate = (oData) => oDispatch(actionReportGetPartnersBalance(oData));

  const aTypes = [
    { label: "Deudores", value: 1 },
    { label: "Acreedores", value: 2 },
  ];

  const [oType, setType] = useState(aTypes[0]);
  const [dDate, setDate] = useState(new Date());
  const [bIncludeBranches, setIncludeBranches] = useState(false);
  const [bShowModal, setShowModal] = useState(false);

  const onExport = () => {
    setShowModal(true);
  };

  const onConfirmExport = () => {
    setShowModal(false);
    onGenerate({
      tipo: oType.value,
      incsuc: bIncludeBranches ? 1 : 0,
      fecha: CastDate(dDate, DATES_FORMATS.MYSQL_SERVER),
    });
  };

  // 1. Mapeamos los datos procesados para el CSV exactamente como se muestran en la grilla
  const aMappedDataForCsv = aData ? aData.map(PartnerBalanceMapper) : [];

  // 2. Definimos las columnas del CSV apuntando a las llaves que devuelve el Mapper
  const csvHeaders = [
    { label: "Código", key: "partner.code" },
    { label: "Socio de negocio", key: "partner.name" },
    { label: "Saldo Pesos", key: "balance.uyu" },
    { label: "Saldo Dólares", key: "balance.usd" },
  ];


  return (
    <section>
      <section className="grid grid-cols-1 sm:grid-cols-2">
        <h1 className="ui-page-title self-center text-center md:text-left">
          Saldo de socios
        </h1>
        <div className="flex flex-col sm:flex-row gap-2 mt-5 mx-auto sm:mr-0 sm:mt-0 sm:ml-auto">
          <CSVLink
            data={aMappedDataForCsv}
            headers={csvHeaders}
            filename={`Saldo_Socios_${oType.label}_${oDayJs(dDate).format("DD-MM-YYYY")}.csv`}
            separator={";"} // Clave para que los Excel de Latam lo separen nativamente en columnas
          >
            <Button
              label="Exportar CSVs"
              type={BUTTON_CONFIG.CLASS.SUCCESS}
              icon={<BiExport className="text-white ml-2 text-lg" />}
            />
          </CSVLink>
          <Button
            label="Generar reporte"
            onClick={onExport}
            type={BUTTON_CONFIG.CLASS.PRIMARY}
            icon={<BiFile className="text-white ml-2 text-lg" />}
            containerClassName="mt-5 mx-auto sm:mr-0 sm:mt-0 sm:ml-auto"
          />
        </div>
      </section>
      <section className="grid grid-cols-1 sm:grid-cols-3 ui-card py-4 px-5 mt-4 gap-5">
        <div className="relative col-span-1 gap-5">
          <ComboBox
            label="Opciones"
            options={aTypes}
            selected={oType}
            onChange={setType}
            emptyLabel="Seleccione una opción"
          />
        </div>
        <div className="relative col-span-1 gap-5">
          <DateInput
            label="Fecha"
            value={dDate}
            onChange={setDate}
            placeholder="Fecha"
            containerClassName="rounded-lg overflow-hidden"
            className="ui-field w-full"
          />
        </div>
        <Checkbox
          checked={bIncludeBranches}
          label="Incluir sucursales"
          containerClassName="flex items-center justify-end pt-8"
          onChange={() => setIncludeBranches(!bIncludeBranches)}
        />
      </section>
      <section className="py-5">
        <Table>
          <Table.Header>
            <Table.Row isHeaderOrFooter>
              <Table.Col scope="col" className="px-3 py-2 text-center">
                Código
              </Table.Col>
              <Table.Col scope="col" className="px-3 py-2 text-center">
                Socio de negocio
              </Table.Col>
              <Table.Col scope="col" className="px-3 py-2 text-right">
                Saldo Pesos
              </Table.Col>
              <Table.Col scope="col" className="px-3 py-2 text-right">
                Saldo Dólares
              </Table.Col>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {aData.length > 0 ? (
              aData.map(PartnerBalanceMapper).map((oItem, nIndex) => (
                <Table.Row key={`${nIndex}`}>
                  <Table.Col
                    scope="row"
                    className="text-xs md:text-sm px-3 py-2 font-medium text-gray-800 dark:text-white whitespace-nowrap text-center"
                  >
                    {oItem.partner?.code}
                  </Table.Col>
                  <Table.Col className="px-3 py-2 text-center">
                    {oItem.partner?.name}
                  </Table.Col>
                  <Table.Col className="px-3 py-2 text-right">
                    {oItem.balance?.uyu}
                  </Table.Col>
                  <Table.Col className="px-3 py-2 text-right">
                    {oItem.balance?.usd}
                  </Table.Col>
                </Table.Row>
              ))
            ) : (
              <Table.Row>
                <Table.Col className="px-3 py-2 text-center" colSpan={7}>
                  <em>Sin resultados</em>
                </Table.Col>
              </Table.Row>
            )}
          </Table.Body>
        </Table>
      </section>

      <ConfirmationModal
        show={bShowModal}
        title="Generar reporte"
        question="¿Está seguro que desea generar el reporte de saldo de socios?"
        message="Esta acción puede tardar unos segundos"
        onConfirm={onConfirmExport}
        onClose={() => setShowModal(false)}
      />

    </section>
  );
};

export default PartnersBalanceReport;
